// blueprintRecovery — the app-scoped blueprint file + reset operations, as pure
// storage functions.
//
// These used to live inside AppMenu.jsx, which is why they were only reachable
// from a floating overlay: they are RECOVERY actions ("my graphs are wrecked, get
// me back to a known state"), so they must keep working when the blueprint editor
// is broken, unmounted, or popped out. Extracting them here makes that
// structural rather than incidental — the menubar calls them directly and never
// depends on an editor being alive.
//
// It also settles a real ambiguity the consolidation inherited. The two menus
// each had a "reset blueprints" with DIFFERENT mechanics:
//
//   AppMenu          cleared every aos-blueprint* key and broadcast
//                    'reload_blueprints'. That empties the graphs — it does not
//                    restore defaults, so you land on blank canvases.
//   BlueprintEditor  cleared the same keys and then navigated to
//                    ?blueprint=blueprints/conversational.json, which makes
//                    App.jsx + BlueprintEditor re-fetch the SHIPPED blueprint
//                    and write it back into both localStorage and graph state.
//
// The editor's mechanic is the one that matches the label, so it is the only one
// that survives: `resetAllBlueprints` clears the keys and returns the URL to
// navigate to. There is exactly one "reset all blueprints" action in the app now.
//
// Every function takes its storage / location as an argument (defaulting to the
// real ones) so `node --test` can drive them with a fake — this module has no
// imports and touches no DOM.

/** Every blueprint localStorage key is namespaced under this prefix — body,
 *  face and events, per-character and legacy-global alike. */
export const BLUEPRINT_KEY_PREFIX = 'aos-blueprint'

/** The blueprint restored by a reset. Shipped in public/blueprints/. */
export const SHIPPED_BLUEPRINT_URL = 'blueprints/conversational.json'

function keysOf(storage) {
  // Object.keys works on the real Storage object and on a plain-object fake.
  return Object.keys(storage ?? {})
}

function defaultStorage() {
  return typeof localStorage !== 'undefined' ? localStorage : null
}

/**
 * Read every blueprint key into a plain object, for the export file.
 *
 * Unparseable values are SKIPPED rather than exported as raw strings: the import
 * side re-serialises with JSON.stringify, so a corrupt entry would round-trip
 * into a double-encoded one. Dropping it keeps the file loadable.
 *
 * @returns {{data: Record<string, any>, skipped: string[]}}
 */
export function collectBlueprintEntries(storage = defaultStorage()) {
  const data = {}
  const skipped = []
  if (!storage) return { data, skipped }
  for (const key of keysOf(storage)) {
    if (!key.startsWith(BLUEPRINT_KEY_PREFIX)) continue
    try {
      data[key] = JSON.parse(storage.getItem(key))
    } catch {
      skipped.push(key)
    }
  }
  return { data, skipped }
}

/**
 * Write an exported bundle back into storage.
 *
 * Only `aos-blueprint*` keys are applied. That filter is the whole safety
 * property of the import: the file is user-supplied, and without it a crafted
 * (or simply mis-saved) bundle could overwrite auth, palette or draft keys.
 *
 * @returns {{applied: string[], ignored: string[]}}
 */
export function applyBlueprintEntries(data, storage = defaultStorage()) {
  const applied = []
  const ignored = []
  if (!storage || !data || typeof data !== 'object') return { applied, ignored }
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith(BLUEPRINT_KEY_PREFIX)) {
      ignored.push(key)
      continue
    }
    storage.setItem(key, JSON.stringify(value))
    applied.push(key)
  }
  return { applied, ignored }
}

/**
 * Remove every blueprint key.
 *
 * A glob rather than a hardcoded list: keys are per-character
 * (`aos-blueprint-v6-<slug>`, `-face-v7-<slug>`, `-events-v7-<slug>`) and older
 * versions linger, so an enumerated list misses most of them.
 *
 * @returns {string[]} the keys removed.
 */
export function clearBlueprintKeys(storage = defaultStorage()) {
  if (!storage) return []
  const removed = keysOf(storage).filter(k => k.startsWith(BLUEPRINT_KEY_PREFIX))
  for (const k of removed) storage.removeItem(k)
  return removed
}

/**
 * The URL that reloads onto the shipped blueprint.
 *
 * Session params are dropped — a reset shouldn't re-apply whatever `?debug=1`
 * happened to be in the address bar — with ONE exception that is load-bearing:
 * **`?character=` must survive.** Boot only skips the launcher on a `?character=`
 * deep link (src/lib/characterSelection.js), so dropping it strands the reset on
 * the character picker instead of reloading the character you just reset.
 *
 * Built by hand rather than with URLSearchParams: that would percent-encode the
 * blueprint path's slash (`blueprint=blueprints%2Fconversational.json`), and
 * every consumer of this URL — docs, tests, humans copying it — knows it with a
 * literal slash.
 *
 * @param {string} pathname
 * @param {string|null} characterId  the active `?character=`, or null.
 */
export function shippedBlueprintUrl(pathname = '/', characterId = null) {
  const prefix = characterId ? `?character=${encodeURIComponent(characterId)}&` : '?'
  return `${pathname}${prefix}blueprint=${SHIPPED_BLUEPRINT_URL}`
}

/**
 * Clear the blueprint keys and report where to navigate next. Does NOT navigate
 * itself — the caller owns that, so this stays testable and so the confirm flow
 * can't be short-circuited by an early location write.
 *
 * `characterId` defaults to the live `?character=` so the reset reloads the
 * character being reset rather than the launcher — see shippedBlueprintUrl.
 *
 * @returns {{removed: string[], nextUrl: string}}
 */
export function resetAllBlueprints(storage = defaultStorage(), pathname = undefined, characterId = undefined) {
  const removed = clearBlueprintKeys(storage)
  const path = pathname ?? (typeof location !== 'undefined' ? location.pathname : '/')
  const id = characterId !== undefined
    ? characterId
    : (typeof location !== 'undefined'
      ? new URLSearchParams(location.search).get('character')
      : null)
  return { removed, nextUrl: shippedBlueprintUrl(path, id) }
}

/**
 * Wipe localStorage entirely — the "clear local cache" nuclear option.
 *
 * Deliberately NOT filtered to `aos-*`: the point is to recover a browser whose
 * stored state is the problem, and a key we forgot to enumerate is exactly the
 * one that would survive and keep the app broken. The binary Cache-Storage
 * buckets are the caller's job (they're async).
 *
 * @returns {string[]} the keys removed.
 */
export function clearAllLocalStorage(storage = defaultStorage()) {
  if (!storage) return []
  // TODO(auth): once auth tokens land in localStorage, exclude their key prefix
  // here so the user doesn't get logged out on every clear.
  const removed = keysOf(storage)
  for (const k of removed) storage.removeItem(k)
  return removed
}
